// helpers/utmTracking.js
//
// UTM-Parameter beim Seitenaufruf auslesen, in First-Party-Cookies persistieren
// und beim Signup an das Backend weitergeben.
//
// Cookies:
//   ael_utm_first  – First-Touch (nur gesetzt wenn noch nicht vorhanden)
//   ael_utm_last   – Last-Touch  (bei jedem Besuch mit UTM-Params ueberschrieben)
//
// Beide laufen nach 30 Tagen ab.

import Cookies from "js-cookie";

const COOKIE_FIRST = "ael_utm_first";
const COOKIE_LAST = "ael_utm_last";
const COOKIE_EXPIRY_DAYS = 30;

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseUtmFromUrl() {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const utm = {};
  let hasAny = false;

  for (const key of UTM_PARAMS) {
    const value = params.get(key);
    if (value) {
      // strip "utm_" prefix for cleaner storage keys
      const shortKey = key.startsWith("utm_") ? key.slice(4) : key;
      utm[shortKey] = value;
      hasAny = true;
    }
  }

  return hasAny ? utm : null;
}

function buildPayload(utmParams) {
  return {
    source: utmParams?.source ?? null,
    medium: utmParams?.medium ?? null,
    campaign: utmParams?.campaign ?? null,
    content: utmParams?.content ?? null,
    term: utmParams?.term ?? null,
    gclid: utmParams?.gclid ?? null,
    fbclid: utmParams?.fbclid ?? null,
    referrer:
      typeof document !== "undefined" && document.referrer
        ? document.referrer
        : null,
    landingPage:
      typeof window !== "undefined" ? window.location.pathname : null,
    firstVisit: new Date().toISOString(),
  };
}

function readCookieJson(name) {
  try {
    const raw = Cookies.get(name);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeCookie(name, value) {
  Cookies.set(name, JSON.stringify(value), {
    expires: COOKIE_EXPIRY_DAYS,
    path: "/",
    sameSite: "Lax",
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Liest UTM-Parameter aus der URL, speichert First-Touch (einmalig) und
 * Last-Touch (bei jedem Besuch mit UTM-Params).
 *
 * Soll bei jedem Seitenaufruf aufgerufen werden, aber NUR wenn der User
 * Analytical-Consent gegeben hat.
 */
export function initUtmTracking() {
  if (typeof window === "undefined") return;

  const utmParams = parseUtmFromUrl();

  // First-Touch: immer setzen wenn noch kein Cookie vorhanden
  // (auch ohne UTM-Params, damit Direct/Organic-Besuche erfasst werden)
  if (!Cookies.get(COOKIE_FIRST)) {
    writeCookie(COOKIE_FIRST, buildPayload(utmParams));
  }

  // Last-Touch: nur ueberschreiben wenn neue UTM-Params vorhanden
  if (utmParams) {
    writeCookie(COOKIE_LAST, buildPayload(utmParams));
  }
}

/**
 * Gibt die First-Touch UTM-Daten zurueck (oder null).
 */
export function getUtmFirst() {
  return readCookieJson(COOKIE_FIRST);
}

/**
 * Gibt die Last-Touch UTM-Daten zurueck (oder null).
 */
export function getUtmLast() {
  return readCookieJson(COOKIE_LAST);
}
